import { memo, useCallback } from "react";
import { generatePath } from "react-router";

import type { OptionStockObject } from "@/lib/gql/__generated__/graphql";

import { PATHS } from "@/routes/paths";

import { StockTableRowDailyItem } from "@/components/features/items/stock/_shared/components/table/row/daily-item";
import { StockTableRow } from "@/components/features/items/stock/_shared/components/table/row/row";
import type { OptionStockDiffStore } from "@/components/features/items/stock/option/model/usecase/use-create-form-model/option-stock-diff-store";

import { Label } from "@/components/ui/label/label";
import { TableBody } from "@/components/ui/table/table";

type Props = {
	items: OptionStockObject[];
	hotelID: string;

	optionStockDiffStore: OptionStockDiffStore;
};

export const OptionStockTableRows = memo(({ items, hotelID, optionStockDiffStore }: Props) => {
	const onSetOptionItemStock = useCallback(
		(input: { optionID: string; subOptionID: string | undefined; date: string }) =>
			(isAvailable: boolean, remainNum: number) => {
				optionStockDiffStore.put({
					optionID: input.optionID,
					subOptionID: input.subOptionID,
					date: input.date,
					isAvailable,
					remainNum,
				});
			},
		[optionStockDiffStore],
	);

	return (
		<TableBody>
			{items.map(({ optionID, name, isInSale, subOptionID, stocks }, idx) => {
				const itemEditPagePath = generatePath(PATHS.HOTEL.ITEMS.OPTION.EDIT.OPTIONS_ID.DEFAULT, {
					hotelID,
					optionID,
				});

				return (
					<StockTableRow
						key={`${optionID}_${idx}`}
						itemName={name}
						itemEditPagePath={itemEditPagePath}
						itemLabelChildren={
							<>
								{!isInSale && <Label variant={"black"} text={"非公開"} size={"xs"} />}
								{!!subOptionID && <Label variant={"purple"} text={"サブオプション"} size={"xs"} />}
							</>
						}
						stockDailyItemChildren={stocks.map((stock, idx) => (
							<StockTableRowDailyItem
								key={`${optionID}-${stock.date}-${idx}`}
								stockDailyItem={stock}
								onSetStock={onSetOptionItemStock({
									optionID,
									subOptionID: subOptionID ?? undefined,
									date: stock.date,
								})}
								currentStockDailyItem={optionStockDiffStore.getCurrentOptionStock({
									subOptionID: subOptionID ?? undefined,
									date: stock.date,
									optionID,
									isAvailable: isInSale,
									remainNum: stock.remainNum,
								})}
							/>
						))}
					/>
				);
			})}
		</TableBody>
	);
});

OptionStockTableRows.displayName = "OptionStockTableRows";
